// Extracted from ClientGoogleFeedPreviewPage.js (Phase 2a split).
import { FIELD_CATALOG } from "./fieldCatalog";
export const ymToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export function lastDayOfMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m, 0).getDate();
  return `${ym}-${String(d).padStart(2, "0")}`;
}

// ---------- Report formatting helpers ----------
//
// Decide how to format a metric value purely from its key. Each Google API
// uses different units (micros, fractions, raw counts), but our backend
// normalises everything to standard numbers — so the choice here is purely
// presentational.

export const CURRENCY_KEYS = new Set([
  "cost", "revenue", "average_cpc", "average_cpm", "average_cpv", "average_cpe",
  "average_purchase_revenue", "arpu", "arppu", "item_revenue", "refunds",
  "shipping_amount", "tax_amount", "value_per_conversion", "value_per_all_conversions",
  "advertiser_ad_cost", "cpm", "playback_based_cpm", "estimated_revenue",
  "estimated_ad_revenue", "gross_revenue", "conversions_value", "all_conversions_value",
  "cost_per_conversion",
]);

export const PERCENT_KEYS = new Set([
  "ctr", "conversion_rate", "engagement_rate", "bounce_rate", "interaction_rate",
  "video_view_rate", "active_view_viewability", "phone_through_rate",
  "annotation_click_through_rate", "annotation_close_rate",
  "card_click_rate", "end_screen_click_rate", "average_view_percentage",
  "cart_to_view_rate", "purchase_to_view_rate", "return_on_ad_spend",
  "search_impression_share", "search_top_impression_share",
  "search_absolute_top_impression_share", "search_rank_lost_impression_share",
  "search_budget_lost_impression_share", "content_impression_share",
  "content_budget_lost_impression_share",
  "video_quartile_p25_rate", "video_quartile_p50_rate",
  "video_quartile_p75_rate", "video_quartile_p100_rate",
]);

export const TIME_SECONDS_KEYS = new Set([
  "average_session_duration", "average_view_duration", "user_engagement_duration",
]);

export const TIME_MINUTES_KEYS = new Set([
  "watch_time_minutes", "red_watch_time_minutes",
]);

export const DECIMAL_KEYS = new Set([
  "avg_position", "position", "events_per_session", "screen_page_views_per_session",
  "sessions_per_user",
]);

export function formatNumber(n, opts = {}) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  if (opts.percent) return `${num.toFixed(2)}%`;
  if (opts.currency) return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(num);
  if (opts.decimal) return num.toFixed(2);
  if (opts.timeSeconds) {
    const sec = Math.round(num);
    if (sec >= 3600) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
    if (sec >= 60) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
    return `${sec}s`;
  }
  if (opts.timeMinutes) {
    if (num >= 60) return `${Math.floor(num / 60)}h ${Math.round(num % 60)}m`;
    return `${Math.round(num)}m`;
  }
  // Integer with thousands separator
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num);
}

export function metricFormat(key) {
  if (CURRENCY_KEYS.has(key)) return { currency: true };
  if (PERCENT_KEYS.has(key))  return { percent: true };
  if (TIME_SECONDS_KEYS.has(key)) return { timeSeconds: true };
  if (TIME_MINUTES_KEYS.has(key)) return { timeMinutes: true };
  if (DECIMAL_KEYS.has(key))  return { decimal: true };
  return {};
}

export function formatMetric(key, value) {
  return formatNumber(value, metricFormat(key));
}

/** Look up the human-readable label for a metric key from the catalog. */
export function humanLabel(serviceKey, fieldKey) {
  // Friendly overrides for common API column names we surface in breakdown
  // tables (these aren't in the catalog because they're row-level, not
  // selectable). Keep snake_case keys matching the API response shapes.
  const COLUMN_LABELS = {
    query: "Query", page: "Page", country: "Country", device: "Device",
    date: "Date", searchAppearance: "Search appearance",
    sessionSource: "Source", sessionMedium: "Medium",
    sessionSourceMedium: "Source / medium",
    sessionCampaignName: "Campaign", sessionDefaultChannelGroup: "Channel",
    pagePath: "Page path", pageTitle: "Page title",
    landingPage: "Landing page", eventName: "Event",
    region: "Region", city: "City", language: "Language",
    userAgeBracket: "Age", userGender: "Gender",
    deviceCategory: "Device", browser: "Browser", operatingSystem: "OS",
    mobileDeviceModel: "Mobile model", screenResolution: "Screen",
    appVersion: "App version", week: "Week", month: "Month",
    hour: "Hour", dayOfWeek: "Day of week", newVsReturning: "New / returning",
    firstUserSource: "First-touch source", firstUserMedium: "First-touch medium",
    firstUserCampaignName: "First-touch campaign",
    firstUserSourceMedium: "First-touch source / medium",
    clicks: "Clicks", impressions: "Impressions", ctr: "CTR",
    position: "Position", cost: "Spend", conversions: "Conv.",
    sessions: "Sessions", activeUsers: "Active users", newUsers: "New users",
    screenPageViews: "Page views", views: "Views", likes: "Likes",
    estimatedMinutesWatched: "Watch time (min)",
    viewerPercentage: "Viewers %",
    shares: "Shares", subscribersGained: "Subs gained",
    video: "Video", deviceType: "Device", subscribedStatus: "Subscribed",
    insightPlaybackLocationType: "Playback location",
    insightTrafficSourceType: "Traffic source", youtubeProduct: "Product",
    liveOrOnDemand: "Live / VOD", ageGroup: "Age", gender: "Gender",
    sharingService: "Sharing service",
    key: "Item",  // generic key used by the Ads `_by()` helper
    type: "Type",
  };
  if (COLUMN_LABELS[fieldKey]) return COLUMN_LABELS[fieldKey];

  const cat = FIELD_CATALOG[serviceKey];
  if (cat) {
    for (const g of cat.groups || []) {
      for (const f of g.fields || []) {
        if (f.key === fieldKey) return f.desc;
      }
    }
  }
  // Strip the "by_" / "top_" prefix and titlecase the rest for unknown keys.
  return fieldKey.replace(/^(by_|top_|first_user_|session_|search_type_)/, "")
                 .replace(/_/g, " ")
                 .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Single KPI tile rendered in the totals grid. */
